import { Box, Flex, Text } from "@chakra-ui/react";
import type { TrackingEventWithUnits } from "@superbright/indexeddb-orm";

import type { FiltersResponse } from "api/schemas";
import { Icon, Tabs } from "components";
import {
  type ResultsTab,
  FAVORITES_TAB,
} from "features/results/types/results.types";
import { Filters } from "pages/Results/filters";
import { useNavigate, useSearchParams } from "shared/Router";
import { useIsMobile } from "utils";

import { useColorModeValue } from "../../../components/ui/color-mode";

import { LoaderBar } from "./LoaderBar";

interface NavbarProps {
  activeTab: ResultsTab;
  setActiveTab: (tab: ResultsTab) => void;
  favoritesCount?: number;
  filtersData?: FiltersResponse | null;
  propertySlug?: string;
  setTrackingEventWithUnits: (event: TrackingEventWithUnits | null) => void;
}

export const Navbar = ({
  activeTab,
  setActiveTab,
  favoritesCount = 0,
  filtersData,
  propertySlug,
  setTrackingEventWithUnits,
}: NavbarProps) => {
  const borderColor = useColorModeValue("gray.200", "gray.900");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // SSR-safe viewport check (defaults to mobile on the server / first render).
  const isWide = !useIsMobile(640);

  return (
    <Box>
      <Flex
        w="100%"
        minH="60px"
        py={4}
        px={4}
        borderBottom={1}
        borderStyle="solid"
        borderColor={borderColor}
        align="center"
        style={{ backgroundColor: "#fff" }}
        mb={{ base: 4, md: "18px" }}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          alignItems={{ base: "start", md: "center" }}
          width="100%"
          gap={2}
        >
          <Box
            flexShrink={0}
            maxWidth={{ base: "100%", md: "284px" }}
            width="100%"
            mr={{ md: "auto" }}
          >
            <Tabs
              key={activeTab}
              defaultIndex={activeTab === "search" ? 0 : 1}
              tabs={[
                {
                  label: "Search",
                  width: isWide ? "140px" : "100%",
                },
                {
                  label: (
                    <Flex align="center" gap="2">
                      <Icon name="heart-gray" />
                      <Text fontSize="medium">{favoritesCount || null}</Text>
                    </Flex>
                  ),
                  width: isWide ? "98px" : "100%",
                },
              ]}
              onChange={(index) => {
                const newTab: ResultsTab =
                  index === 0 ? "search" : FAVORITES_TAB;
                setActiveTab(newTab);
                if (index === 0) {
                  const qs = searchParams.toString();
                  navigate(`/${propertySlug}/results${qs ? `?${qs}` : ""}`);
                } else {
                  navigate(`/${propertySlug}/results/${FAVORITES_TAB}`);
                }
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </Box>

          {/* Filters fade in/out based on active tab */}
          {activeTab === "search" && (
            <Box
              flex="1"
              display="flex"
              justifyContent="center"
              px={{ base: 0, md: 4 }}
              mr={{ base: "0", md: "500px" }}
            >
              <Filters
                filtersData={filtersData}
                setTrackingEventWithUnits={setTrackingEventWithUnits}
                propertySlug={propertySlug}
              />
            </Box>
          )}
        </Flex>
      </Flex>
      <LoaderBar />
    </Box>
  );
};
